import type { TimelineRow } from '@tabel/shared/agent-events/timeline';
import { useVirtualizer } from '@tanstack/react-virtual';
import { type ReactNode, memo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Skeleton } from '../ui';
import { DraftChangeCard } from './DraftChangeCard';
import { ProjectCard } from './ProjectCard';
import { RequestHistory } from './RequestCard';
import { AssistantRow } from './rows/AssistantRow';
import { SampleRow } from './rows/SampleRow';
import { StepRow } from './rows/StepRow';
import { SystemRow } from './rows/SystemRow';
import { TurnRow } from './rows/TurnRow';
import { UserRow } from './rows/UserRow';
import { LABEL } from './texts';

/*
 * Лента разговора (T074; contracts/ui.md §2, §6; FR-035, FR-036, FR-040; мокап Main.dc.html `.feed`).
 * - Роль `log`, `aria-live="polite"`: чтение экрана объявляет готовые ответы и запросы агента. Пока агент пишет ответ
 *   или работает, лента `aria-busy` — читается итог, а не каждая буква.
 * - Строки — по ключу; строка перерисовывается, только когда сменился её объект (слияние догрузки оставляет
 *   неизменённые строки теми же объектами). Работа агента, которая ещё идёт, своей строки не даёт: видны её шаги;
 *   закрытая — кнопка «Работал N мин · K шагов».
 * - От 20 строк — виртуализация `@tanstack/react-virtual`: в документе только видимые строки и запас.
 * - Прокрутка стоит у низа, пока человек сам не прокрутил вверх. Панель с нулевой высотой (анимация открытия)
 *   прокрутку не сбрасывает. Позиция и раскрытые работы агента помнятся для каждого разговора, пока вкладка открыта.
 */

/** С этого числа строк лента виртуальная. */
export const VIRTUAL_FROM = 20;
/** Ближе к низу, чем на столько, — «у низа». */
const NEAR_BOTTOM = 48;

interface Memory {
  top: number;
  stick: boolean;
  expanded: readonly string[];
}
const MEMORY = new Map<string, Memory>();

/** Забыть позиции и раскрытия (для тестов). */
export function forgetFeedMemory(): void {
  MEMORY.clear();
}

/** Сообщение, принятое сервером, пока агент не начал работу: строки с сервера ещё нет. */
export interface PendingRow {
  key: string;
  text: string;
  note: 'sent_now' | null;
  /** Номер ленты, когда сообщение ушло. */
  seq: number;
}

type Item = { key: string; row: TimelineRow; pending: boolean };

export interface FeedProps {
  threadId: string | null;
  rows: readonly TimelineRow[];
  pending: readonly PendingRow[];
  /** Строка, которую сейчас показывает полоса состояния (ошибка модели), — в ленте её нет. */
  hiddenKey: string | null;
  /** Агент пишет ответ или работает. */
  busy: boolean;
  /** Пока лента грузится — заглушка в форме строк. */
  loading: boolean;
  /** До первого сообщения — приветствие; разговора нет — сообщение об этом. */
  empty: ReactNode;
  onOpenProject: (planId: number) => void;
  layout: 'panel' | 'page';
}

const pendingRow = (p: PendingRow): Extract<TimelineRow, { kind: 'user' }> => ({
  key: `msg:${p.key}`,
  kind: 'user',
  seq: p.seq,
  updatedSeq: p.seq,
  runId: null,
  text: p.text,
  note: p.note,
});

const RowView = memo(function RowView({
  row,
  pending,
  threadId,
  expanded,
  onToggle,
  onOpenProject,
}: {
  row: TimelineRow;
  pending: boolean;
  threadId: string;
  expanded: boolean;
  onToggle: (key: string) => void;
  onOpenProject: (planId: number) => void;
}) {
  switch (row.kind) {
    case 'user':
      return <UserRow row={row} pending={pending} />;
    case 'assistant':
      return <AssistantRow row={row} />;
    case 'step':
      return <StepRow row={row} />;
    case 'sample':
      return <SampleRow row={row} />;
    case 'system':
      return <SystemRow row={row} />;
    case 'request':
      return <RequestHistory row={row} />;
    case 'draft_change':
      return <DraftChangeCard row={row} />;
    case 'project':
      return <ProjectCard row={row} onOpen={onOpenProject} />;
    case 'turn':
      return (
        <TurnRow
          row={row}
          threadId={threadId}
          expanded={expanded}
          onToggle={onToggle}
          renderRow={(r) => (
            <RowView
              row={r}
              pending={false}
              threadId={threadId}
              expanded={false}
              onToggle={onToggle}
              onOpenProject={onOpenProject}
            />
          )}
        />
      );
  }
});

/** Отступ над строкой: шаги одной работы — плотно (мокап `.work`), остальное — 12 px. */
function gapOf(items: readonly Item[], i: number): string {
  if (i === 0) return '';
  const cur = items[i]?.row.kind;
  const prev = items[i - 1]?.row.kind;
  const tight = (k: string | undefined) => k === 'step' || k === 'sample';
  return tight(cur) && tight(prev) ? 'pt-0.5' : 'pt-3';
}

const SCROLLER = {
  // В правой панели лента — во всё тело панели: отступы тела SidePanel (px-6 pt-5 pb-6, на телефоне p-4) переходят
  // внутрь ленты, и полоса прокрутки — у края панели.
  panel: '-mx-6 -mt-5 -mb-6 px-6 pt-5 pb-3 max-sm:-m-4 max-sm:px-4 max-sm:pt-4 max-sm:pb-3',
  page: 'px-6 pt-5 pb-3 max-sm:px-4 max-sm:pt-4',
} as const;

export function Feed(p: FeedProps) {
  const ref = useRef<HTMLDivElement>(null);
  const memKey = p.threadId ?? '';
  const memory = MEMORY.get(memKey);
  const stick = useRef(memory?.stick ?? true);
  const restored = useRef<string | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set(memory?.expanded ?? []));
  const [expandedFor, setExpandedFor] = useState(memKey);
  if (expandedFor !== memKey) {
    setExpandedFor(memKey);
    setExpanded(new Set(MEMORY.get(memKey)?.expanded ?? []));
  }

  const remember = useCallback(
    (patch: Partial<Memory>) => {
      if (!memKey) return;
      const prev = MEMORY.get(memKey) ?? { top: 0, stick: true, expanded: [] };
      MEMORY.set(memKey, { ...prev, ...patch });
    },
    [memKey],
  );

  const onToggle = useCallback(
    (key: string) => {
      stick.current = false;
      setExpanded((s) => {
        const next = new Set(s);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        remember({ expanded: [...next], stick: false, top: ref.current?.scrollTop ?? 0 });
        return next;
      });
    },
    [remember],
  );

  const items: Item[] = [];
  for (const r of p.rows) {
    if (r.key === p.hiddenKey) continue;
    if (r.kind === 'turn' && !r.collapsed) continue;
    items.push({ key: r.key, row: r, pending: false });
  }
  const known = new Set(items.map((i) => i.key));
  for (const x of p.pending) {
    const row = pendingRow(x);
    if (!known.has(row.key)) items.push({ key: row.key, row, pending: true });
  }

  const virtual = items.length >= VIRTUAL_FROM;
  const v = useVirtualizer({
    count: virtual ? items.length : 0,
    getScrollElement: () => ref.current,
    estimateSize: () => 56,
    overscan: 8,
    getItemKey: (i) => items[i]?.key ?? i,
  });

  // Позиция: у низа, если человек не прокрутил вверх. Восстановление — один раз, когда лента загружена.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || p.loading) return;
    if (restored.current !== memKey) {
      restored.current = memKey;
      const saved = MEMORY.get(memKey);
      if (saved && !saved.stick) {
        stick.current = false;
        el.scrollTop = saved.top;
        return;
      }
      stick.current = true;
    }
    if (stick.current) el.scrollTop = el.scrollHeight;
  });

  const onScroll = () => {
    const el = ref.current;
    // Панель ещё без высоты (анимация открытия): позицию не меняем.
    if (!el || el.clientHeight === 0) return;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM;
    remember({ top: el.scrollTop, stick: stick.current });
  };

  const view = (it: Item, i: number) => (
    <div key={it.key} data-row-key={it.key} data-kind={it.row.kind} className={gapOf(items, i)}>
      <RowView
        row={it.row}
        pending={it.pending}
        threadId={p.threadId ?? ''}
        expanded={expanded.has(it.key)}
        onToggle={onToggle}
        onOpenProject={p.onOpenProject}
      />
    </div>
  );

  const col = p.layout === 'page' ? 'mx-auto w-full max-w-[760px]' : 'w-full';
  return (
    <div
      ref={ref}
      role="log"
      aria-live="polite"
      aria-busy={p.busy}
      aria-label={LABEL.feed}
      data-virtual={virtual ? 'yes' : 'no'}
      onScroll={onScroll}
      className={`flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-contain ${SCROLLER[p.layout]}`}
    >
      {p.loading ? (
        <div aria-busy="true" aria-label={LABEL.loading} className={`mt-auto flex flex-col gap-3 ${col}`}>
          <Skeleton className="ml-auto h-9 w-3/5" />
          <Skeleton className="h-3.5 w-4/5" />
          <Skeleton className="h-3.5 w-11/12" />
          <Skeleton className="h-3.5 w-1/2" />
        </div>
      ) : items.length === 0 ? (
        <div className={`flex min-h-full flex-1 flex-col ${col}`}>{p.empty}</div>
      ) : virtual ? (
        <div className={col}>
          <div className="relative w-full" style={{ height: v.getTotalSize() }}>
            {v.getVirtualItems().map((vi) => {
              const it = items[vi.index];
              if (!it) return null;
              return (
                <div
                  key={vi.key}
                  data-index={vi.index}
                  ref={v.measureElement}
                  className="absolute top-0 left-0 w-full"
                  style={{ transform: `translateY(${vi.start}px)` }}
                >
                  {view(it, vi.index)}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className={`mt-auto flex flex-col ${col}`}>{items.map(view)}</div>
      )}
    </div>
  );
}
